import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import moment from "moment";
import Swal from "sweetalert2";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { Button, Col, Input, Label, Modal, ModalBody, ModalHeader, Row, Table } from "reactstrap";
import { AuthContext } from "../../../contexts/AuthContext";
import Header from "../../../layout/Header/Header";
import { pdfFooter, fmtMoney, pdfDocumentoTabla } from "../../../../pdfUtils";

const headers = () => ({ Authorization: `Bearer: ${localStorage.getItem("app_token")}` });
const fmtN = (v) => new Intl.NumberFormat("es-MX").format(v || 0);

function ListadoCompras() {
  const { user } = useContext(AuthContext);
  const URL_COMPRAS = process.env.REACT_APP_URL_COMPRAS;
  const URL_ACUM = process.env.REACT_APP_URL_COMPRAS_ACUMULADOS;

  const [compras, setCompras] = useState([]);
  const [inicio, setInicio] = useState(moment().subtract(3, "months").format("YYYY-MM-DD"));
  const [fin, setFin] = useState(moment().format("YYYY-MM-DD"));
  const [buscar, setBuscar] = useState("");
  const [estado, setEstado] = useState("Si");
  const [detalle, setDetalle] = useState(null);

  const cargar = () => axios.get(`${URL_COMPRAS}Fechas/${inicio}/${fin}`, { headers: headers() })
    .then((r) => setCompras(r.data.sort((a, b) => b.idCompras - a.idCompras)))
    .catch(console.log);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { cargar(); }, [inicio, fin]);

  const visibles = useMemo(() => {
    const q = buscar.toLowerCase();
    return compras.filter((c) =>
      (!estado || c.is_active === estado) &&
      (!q || `${c.idCompras} ${c.factura} ${c.ordenesCompra?.idOrdenCompra} ${c.ordenesCompra?.proveedores?.[0]?.nombre_comercial}`.toLowerCase().includes(q)));
  }, [compras, buscar, estado]);

  const totales = visibles.reduce((t, c) => ({ b: t.b + c.total_cantidad, i: t.i + (c.total_costo || 0) }), { b: 0, i: 0 });

  const renglones = async (c) => {
    const r = await axios.get(`${URL_ACUM}/compra/${c._id}`, { headers: headers() });
    return r.data;
  };
  const verDetalle = async (c) => setDetalle({ compra: c, lineas: await renglones(c) });

  const pdf = async (c) => {
    const lineas = await renglones(c);
    const doc = new jsPDF();
    pdfDocumentoTabla(doc, {
      titulo: "Recepcion",
      folio: c.idCompras,
      fecha: c.fecha,
      datos: [
        { titulo: "Proveedor", lineas: [c.ordenesCompra?.proveedores?.[0]?.nombre_comercial] },
        { titulo: "Datos", lineas: [`OC ${c.ordenesCompra?.idOrdenCompra}`, `Factura ${c.factura}`] },
      ],
      head: ["Vino", "Botellas", "Costo", "Importe MXN"],
      body: lineas.map((l) => [`${l.articulos[0].codigo} ${l.articulos[0].nombre}`, fmtN(l.cantidad), fmtMoney(l.precio), fmtMoney(l.precioMX * l.cantidad)]),
      totales: [["Botellas", fmtN(c.total_cantidad)], ["Total", fmtMoney(c.total_costo)]],
    });
    pdfFooter(doc);
    doc.save(`Recepcion-${c.idCompras}.pdf`);
  };

  const cancelar = async (c) => {
    const ok = await Swal.fire({
      title: `¿Cancelar la recepción ${c.idCompras}?`,
      text: "Saca el vino del inventario, regresa la orden a pendiente y cancela la cuenta por pagar.",
      icon: "warning", showCancelButton: true, confirmButtonText: "Sí, cancelar", cancelButtonText: "No",
    });
    if (!ok.value) return;
    try {
      await axios.patch(`${URL_COMPRAS}/cancelarRecepcion/${c._id}`, {}, { headers: headers() });
      Swal.fire("Recepción cancelada", "", "success");
      cargar();
    } catch (error) {
      Swal.fire({ icon: "error", title: "No se canceló", text: error.response?.data?.message || "Intenta de nuevo" });
    }
  };

  if (!user.menu_compras) return <Header />;

  return (
    <>
      <Header />
      <div className="card container col-12">
        <div className="page-header">
          <div>
            <h3 className="page-title">Recepciones</h3>
            <p className="page-subtitle">{fmtN(totales.b)} botellas · {fmtMoney(totales.i)} MXN</p>
          </div>
          {user.compras_create ? <a className="btn btn-sm btn-primary" href="/ComprasCreate">Nueva recepción</a> : null}
        </div>
        <Row className="filter-row">
          <Col md={2}><Label>Desde</Label><Input type="date" value={inicio} onChange={(e) => setInicio(e.target.value)} /></Col>
          <Col md={2}><Label>Hasta</Label><Input type="date" value={fin} onChange={(e) => setFin(e.target.value)} /></Col>
          <Col md={2}>
            <Label>Estado</Label>
            <Input type="select" value={estado} onChange={(e) => setEstado(e.target.value)}>
              <option value="Si">Activas</option><option value="No">Canceladas</option><option value="">Todas</option>
            </Input>
          </Col>
          <Col md={4}><Label>Buscar</Label><Input value={buscar} onChange={(e) => setBuscar(e.target.value)} placeholder="Folio, factura, orden o proveedor" /></Col>
        </Row>
        <Table size="sm" responsive className="mt-3">
          <thead>
            <tr>
              <th>Folio</th><th>Fecha</th><th>OC</th><th>Proveedor</th><th>Factura</th><th>Almacén</th>
              <th className="text-right">Botellas</th><th className="text-right">Importe MXN</th><th>Estado</th><th></th>
            </tr>
          </thead>
          <tbody>
            {visibles.map((c) => (
              <tr key={c._id}>
                <td>{c.idCompras}</td>
                <td>{c.fecha}</td>
                <td>{c.ordenesCompra?.idOrdenCompra}</td>
                <td>{c.ordenesCompra?.proveedores?.[0]?.nombre_comercial}</td>
                <td>{c.factura}</td>
                <td>{c.areas?.[0]?.name}</td>
                <td className="text-right">{fmtN(c.total_cantidad)}</td>
                <td className="text-right td-money">{fmtMoney(c.total_costo)}</td>
                <td><span className={`badge-soft-${c.is_active === "Si" ? "success" : "danger"}`}>{c.is_active === "Si" ? "Activa" : "Cancelada"}</span></td>
                <td className="text-right" style={{ whiteSpace: "nowrap" }}>
                  <Button size="sm" color="secondary" onClick={() => verDetalle(c)} title="Detalle"><i className="fas fa-eye" /></Button>{" "}
                  <Button size="sm" color="secondary" disabled={!user.imprimir} onClick={() => pdf(c)} title="PDF"><i className="far fa-file-pdf" /></Button>{" "}
                  {c.is_active === "Si" && user.compras_cancelar && <Button size="sm" color="danger" onClick={() => cancelar(c)} title="Cancelar"><i className="fas fa-ban" /></Button>}
                </td>
              </tr>
            ))}
            {visibles.length === 0 && <tr><td colSpan={10} className="text-center">Sin recepciones en el periodo</td></tr>}
          </tbody>
        </Table>
      </div>

      <Modal size="lg" isOpen={!!detalle} toggle={() => setDetalle(null)}>
        <ModalHeader toggle={() => setDetalle(null)}>Recepción {detalle?.compra.idCompras}</ModalHeader>
        <ModalBody>
          {detalle?.compra.total_gastos ? <p>Gastos de importacion: {fmtMoney(detalle.compra.total_gastos)}</p> : null}
          <Table size="sm" responsive>
            <thead><tr><th>Vino</th><th className="text-right">Botellas</th><th className="text-right">Costo</th><th className="text-right">Costo real</th><th className="text-right">Importe MXN</th></tr></thead>
            <tbody>
              {(detalle?.lineas || []).map((l) => (
                <tr key={l._id}>
                  <td>{l.articulos[0].codigo} {l.articulos[0].nombre}</td>
                  <td className="text-right">{fmtN(l.cantidad)}</td>
                  <td className="text-right td-money">{fmtMoney(l.precio)}</td>
                  <td className="text-right td-money">{fmtMoney(l.costoReal || l.precioMX)}</td>
                  <td className="text-right td-money">{fmtMoney(l.precioMX * l.cantidad)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </ModalBody>
      </Modal>
    </>
  );
}

export default ListadoCompras;
